import { useCallback, useEffect, useState } from 'react'
import { findPostHogService, grantPostHogConsent, showPostHogConsentDetails, subscribeToConsentChanges } from './consent'
import { isPostHogConsentRequired, refreshPostHogConsent } from './posthog-tracking'

/**
 * In-app notice asking for the PostHog consent (DO-13470).
 *
 * Usercentrics does not re-prompt users who consented before the `PostHog` service was added. The
 * notice fills that gap: it is shown while PostHog is configured, the CMP knows the service, the
 * user has already answered the Usercentrics banner and the consent is not given. While the
 * Usercentrics banner itself is still open the notice stays hidden. "Ja gerne" writes the consent
 * into Usercentrics, "Details" opens the Usercentrics service details, the close button hides the
 * notice locally only.
 *
 * Bump `POSTHOG_CONSENT_NOTICE_VERSION` whenever the notice text changes: a locally dismissed
 * notice comes back once for the new text.
 */
export const POSTHOG_CONSENT_NOTICE_VERSION = 1

const STORAGE_KEY = 'posthogConsentNotice'

/** How often the CMP is re-read while it has not answered yet (backoff: n seconds). */
const MAX_LOOKUP_RETRIES = 5

interface NoticeDismissal {
  dismissedAt: string
  noticeVersion: number
}

const readDismissal = (): NoticeDismissal | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) {
      return null
    }
    const { dismissedAt, noticeVersion } = parsed as Record<string, unknown>
    if (typeof dismissedAt !== 'string' || typeof noticeVersion !== 'number') {
      return null
    }
    return { dismissedAt, noticeVersion }
  } catch {
    return null
  }
}

const isNoticeDismissed = (): boolean => readDismissal()?.noticeVersion === POSTHOG_CONSENT_NOTICE_VERSION

const dismissNotice = (): void => {
  try {
    const dismissal: NoticeDismissal = {
      dismissedAt: new Date().toISOString(),
      noticeVersion: POSTHOG_CONSENT_NOTICE_VERSION,
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissal))
  } catch {
    // storage unavailable: the notice simply shows again on the next start
  }
}

export interface PostHogConsentNoticeState {
  visible: boolean
  accept: () => Promise<void>
  showDetails: () => Promise<void>
  dismiss: () => void
}

export const usePostHogConsentNotice = (): PostHogConsentNoticeState => {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!isPostHogConsentRequired() || isNoticeDismissed()) {
      return
    }

    let cancelled = false
    let retries = 0
    let retryTimer: number | null = null

    const evaluate = () => {
      findPostHogService().then(service => {
        if (cancelled) {
          return
        }
        if (service === null) {
          if (retries < MAX_LOOKUP_RETRIES) {
            retries++
            retryTimer = window.setTimeout(evaluate, 1000 * retries)
          }
          return
        }
        setVisible(service !== undefined && !service.given && !service.decisionPending)
      }).catch(() => {
        // treated like "no answer": stay hidden
      })
    }

    // subscribe first, then read: a CMP that initialises in between is not missed
    const unsubscribe = subscribeToConsentChanges(evaluate)
    evaluate()

    return () => {
      cancelled = true
      unsubscribe()
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer)
      }
    }
  }, [])

  const accept = useCallback(async() => {
    const granted = await grantPostHogConsent()
    if (!granted) {
      console.error('PostHog consent could not be written to Usercentrics')
      return
    }
    setVisible(false)
    refreshPostHogConsent()
  }, [])

  const showDetails = useCallback(() => showPostHogConsentDetails(), [])

  const dismiss = useCallback(() => {
    dismissNotice()
    setVisible(false)
  }, [])

  return { visible, accept, showDetails, dismiss }
}
